/*
 * PRD-037 — Layout PROPIO del portal del partner (app separada). Top-nav V4 + navegacion entre
 * paginas de gestion + Outlet. No depende del layout del panel.
 *
 * Responsive: en escritorio el nav es una barra horizontal; en celular/tablet (<1024) el nav
 * colapsa tras un boton hamburguesa que despliega los links + el nombre + "Salir" en columna.
 */
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { usePartnerAuth } from './PartnerAuthContext';
import { useDesktop } from '../utils/hooks/useDesktop';
import { C } from './ui';

const navItem = (active: boolean, isDesktop: boolean) => ({
  color: active ? C.white : '#B7C9D8',
  fontSize: isDesktop ? 13 : 15,
  fontWeight: active ? 600 : 500,
  textDecoration: 'none',
  display: isDesktop ? undefined : 'block',
  padding: isDesktop ? '6px 2px' : '10px 2px',
  // Canon Tabs: indicador activo = borderBottom 3px teal (#4298B5). En el header oscuro el texto
  // activo queda en blanco; el subrayado teal hace de affordance de "tab activo".
  borderBottom: isDesktop ? (active ? `3px solid ${C.secondary}` : '3px solid transparent') : 'none',
});

const logoutBtnStyle = {
  fontFamily: 'Poppins, sans-serif',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.03em',
  color: C.white,
  background: 'transparent',
  border: '1.5px solid #4F6373',
  borderRadius: 6,
  padding: '6px 14px',
  cursor: 'pointer',
};

const PartnerLayout = () => {
  const { partner, logout } = usePartnerAuth();
  const isDesktop = useDesktop();
  const [menuOpen, setMenuOpen] = useState(false);

  const links = (
    <>
      <NavLink
        to="/partner"
        end
        style={({ isActive }) => navItem(isActive, isDesktop)}
        onClick={() => setMenuOpen(false)}
      >
        Inicio
      </NavLink>
      <NavLink
        to="/partner/comercios"
        style={({ isActive }) => navItem(isActive, isDesktop)}
        onClick={() => setMenuOpen(false)}
      >
        Comercios
      </NavLink>
      <NavLink
        to="/partner/comercios/nuevo"
        style={({ isActive }) => navItem(isActive, isDesktop)}
        onClick={() => setMenuOpen(false)}
      >
        Nuevo comercio
      </NavLink>
    </>
  );

  return (
    <div style={{ minHeight: '100vh', background: C.pageBg, fontFamily: 'Poppins, sans-serif', color: C.primary }}>
      <header style={{ background: C.primary, color: C.white }}>
        <div
          style={{
            padding: isDesktop ? '0 24px' : '0 14px',
            height: 56,
            display: 'flex',
            alignItems: 'center',
            gap: isDesktop ? 28 : 12,
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 16, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
            Plick <span style={{ color: C.secondaryLight ?? C.secondary }}>Partners</span>
          </span>

          {isDesktop ? (
            <>
              <nav style={{ display: 'flex', gap: 22, alignItems: 'center', flex: 1 }}>{links}</nav>
              <span style={{ fontSize: 13, color: '#B7C9D8', whiteSpace: 'nowrap' }}>
                {partner?.nombre || partner?.usuario}
              </span>
              <button type="button" onClick={logout} style={logoutBtnStyle}>
                Salir
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Menú"
              aria-expanded={menuOpen}
              style={{
                marginLeft: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                background: 'transparent',
                border: '1.5px solid #4F6373',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke={C.white}
                strokeWidth="2"
                strokeLinecap="round"
              >
                {menuOpen ? (
                  <>
                    <line x1="5" y1="5" x2="19" y2="19" />
                    <line x1="19" y1="5" x2="5" y2="19" />
                  </>
                ) : (
                  <>
                    <line x1="4" y1="7" x2="20" y2="7" />
                    <line x1="4" y1="12" x2="20" y2="12" />
                    <line x1="4" y1="17" x2="20" y2="17" />
                  </>
                )}
              </svg>
            </button>
          )}
        </div>

        {!isDesktop && menuOpen ? (
          <nav
            style={{
              display: 'flex',
              flexDirection: 'column',
              padding: '4px 16px 14px',
              borderTop: '1px solid #34465480',
            }}
          >
            {links}
            <span style={{ fontSize: 13, color: '#B7C9D8', margin: '12px 0 10px' }}>
              {partner?.nombre || partner?.usuario}
            </span>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                logout();
              }}
              style={{ ...logoutBtnStyle, alignSelf: 'flex-start' }}
            >
              Salir
            </button>
          </nav>
        ) : null}
      </header>

      <main style={{ padding: isDesktop ? '24px 16px' : '16px 12px' }}>
        <div style={{ width: '100%', maxWidth: 880, margin: '0 auto' }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default PartnerLayout;
